import React, { useState, useEffect } from 'react';
import { X, Save, Building2, Trash2, Search, AlertCircle, Settings2, Tag, Users2, Activity, Plus, Briefcase } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../context/AuthContext';
import { Input } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import { SearchableSelect } from '../../components/ui/SearchableSelect';
import { Engagement, TrackingMode } from '../../types';

interface EngagementFormModalProps {
  programId: string;
  engagement?: Engagement | null;
  onClose: () => void;
}

export const EngagementFormModal: React.FC<EngagementFormModalProps> = ({ programId, engagement, onClose }) => {
  const { profile } = useAuth();
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    name: '',
    program_id: programId,
    tracking_mode: 'roster' as TrackingMode,
    audience_type_id: '',
    start_date: '',
    end_date: '',
    description: '',
    status: 'in development',
    capacity: 0,
    category_id: '',
    region_id: ''
  });

  const [involvements, setInvolvements] = useState<{organization_id: string, role_id: string, name?: string}[]>([]);
  const [orgSearch, setOrgSearch] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Lookups
  const { data: allPrograms } = useQuery({
    queryKey: ['all-programs-lookup'],
    queryFn: async () => {
      const { data } = await supabase.from('programs').select('id, name').order('name');
      return (data || []).map(p => ({ id: p.id, name: p.name }));
    }
  });

  const { data: categories } = useQuery({ 
    queryKey: ['lookup', 'engagement_categories'], 
    queryFn: async () => (await supabase.from('engagement_categories').select('*')).data || [] 
  });

  const { data: audienceTypes } = useQuery({ 
    queryKey: ['lookup', 'audience_types'], 
    queryFn: async () => (await supabase.from('audience_types').select('*')).data || [] 
  });

  const { data: regions } = useQuery({ 
    queryKey: ['lookup', 'regions'], 
    queryFn: async () => (await supabase.from('regions').select('*').order('label')).data || [] 
  });

  const { data: tagGroups } = useQuery({ 
    queryKey: ['lookup', 'engagement_tag_groups_with_tags'], 
    queryFn: async () => (await supabase.from('engagement_tag_groups').select('*, engagement_tags(*)')).data || [] 
  });
  
  const { data: assignedTags } = useQuery({
    queryKey: ['engagement-tags', engagement?.id],
    queryFn: async () => {
      if (!engagement) return [];
      const { data } = await supabase.from('engagement_tag_links').select('tag_id').eq('engagement_id', engagement.id);
      return (data || []).map(t => t.tag_id);
    },
    enabled: !!engagement
  });

  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);

  useEffect(() => {
    if (assignedTags) {
      setSelectedTagIds(assignedTags);
    }
  }, [assignedTags]);
  
  const { data: orgRoles } = useQuery({ 
    queryKey: ['lookup', 'organization_roles'], 
    queryFn: async () => (await supabase.from('organization_roles').select('*')).data || [] 
  });

  const { data: orgSearchResults } = useQuery({
    queryKey: ['orgsSearch', orgSearch],
    queryFn: async () => {
      if (orgSearch.length < 2) return [];
      const { data } = await supabase.from('organizations').select('id, name').ilike('name', `%${orgSearch}%`).limit(5);
      return data || [];
    },
    enabled: orgSearch.length >= 2
  });

  useEffect(() => {
    if (engagement) {
      setFormData({
        name: engagement.name,
        program_id: engagement.program_id,
        tracking_mode: engagement.tracking_mode,
        audience_type_id: engagement.audience_type_id || '',
        start_date: engagement.start_date || '',
        end_date: engagement.end_date || '',
        description: engagement.description || '',
        status: engagement.status || 'in development',
        capacity: engagement.capacity || 0,
        category_id: engagement.category_id || '',
        region_id: engagement.region_id || ''
      });
      
      const loadInvolvements = async () => {
        const { data } = await supabase
          .from('engagement_organizations')
          .select('organization_id, role_id, organizations:organization_id(name)')
          .eq('engagement_id', engagement.id);
        
        if (data) {
          setInvolvements(data.map((eo: any) => ({
            organization_id: eo.organization_id,
            role_id: eo.role_id,
            name: eo.organizations?.name
          })));
        }
      };
      loadInvolvements();
    }
  }, [engagement]);

  const addInvolvement = (org: any) => {
    if (involvements.some(inv => inv.organization_id === org.id)) return;
    const defaultRole = orgRoles?.[0]?.id || '';
    setInvolvements([...involvements, { organization_id: org.id, role_id: defaultRole, name: org.name }]);
    setOrgSearch('');
  };

  const removeInvolvement = (orgId: string) => {
    setInvolvements(involvements.filter(inv => inv.organization_id !== orgId));
  };

  const updateInvolvementRole = (orgId: string, roleId: string) => {
    setInvolvements(involvements.map(inv => inv.organization_id === orgId ? { ...inv, role_id: roleId } : inv));
  };

  const upsertEngagementMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      setError(null);
      if (!data.start_date || !data.end_date) {
        throw new Error('Start and end dates are mandatory for all engagements.');
      }
      if (new Date(data.end_date) < new Date(data.start_date)) {
        throw new Error('End date cannot be earlier than start date.');
      }
      let engId = engagement?.id;
      const payload = {
        name: data.name,
        tracking_mode: data.tracking_mode,
        audience_type_id: data.audience_type_id || null,
        start_date: data.start_date || null,
        end_date: data.end_date || null,
        description: data.description,
        status: data.status,
        capacity: data.capacity,
        category_id: data.category_id || null,
        region_id: data.region_id || null,
        program_id: data.program_id,
        created_by_email: profile?.email
      };
      
      if (engagement) {
        const { error: uErr } = await supabase.from('engagements').update(payload).eq('id', engagement.id);
        if (uErr) throw uErr;
      } else {
        const { data: newEng, error: iErr } = await supabase.from('engagements').insert([payload]).select().single();
        if (iErr) throw iErr;
        engId = newEng.id;

        // Auto-create "Session One" for new engagements
        const sessionPayload = {
          engagement_id: engId,
          name: 'Session One',
          session_datetime: data.start_date ? new Date(data.start_date).toISOString() : new Date().toISOString(),
          status: 'upcoming',
          created_by_email: profile?.email
        };
        await supabase.from('sessions').insert([sessionPayload]);
      }

      // Sync Tags - Ensure engId is captured
      if (engId) {
        await supabase.from('engagement_tag_links').delete().eq('engagement_id', engId);
        if (selectedTagIds.length > 0) {
          const tagPayload = selectedTagIds.map(tid => ({
            engagement_id: engId,
            tag_id: tid
          }));
          const { error: tErr } = await supabase.from('engagement_tag_links').insert(tagPayload);
          if (tErr) throw tErr;
        }
      }

      await supabase.from('engagement_organizations').delete().eq('engagement_id', engId!);
      if (involvements.length > 0) {
        const involvementPayload = involvements.map(inv => ({ 
          engagement_id: engId!,
          organization_id: inv.organization_id,
          role_id: inv.role_id
        }));
        await supabase.from('engagement_organizations').insert(involvementPayload);
      }
      return engId;
    },
    onSuccess: (mutatedId) => {
      const engId = mutatedId || engagement?.id;
      queryClient.invalidateQueries({ queryKey: ['program', programId] });
      queryClient.invalidateQueries({ queryKey: ['program', formData.program_id] });
      queryClient.invalidateQueries({ queryKey: ['engagement-core', engId] });
      queryClient.invalidateQueries({ queryKey: ['engagement', engId] });
      queryClient.invalidateQueries({ queryKey: ['engagement-tags-detailed', engId] });
      queryClient.invalidateQueries({ queryKey: ['engagement-partners', engId] });
      onClose();
    },
    onError: (err: any) => setError(err.message)
  });

  const toggleTag = (tagId: string) => {
    setSelectedTagIds(prev => 
      prev.includes(tagId) ? prev.filter(id => id !== tagId) : [...prev, tagId]
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
      <div className="bg-white w-full max-w-2xl rounded shadow-2xl border border-slate-200 overflow-hidden flex flex-col max-h-[90vh]">
        <div className="px-8 py-6 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
          <h2 className="text-lg font-black uppercase text-slate-900 tracking-tight flex items-center gap-3">
            <Settings2 size={20} className="text-blue-600" />
            {engagement ? 'Modify Engagement' : 'Initialize Engagement'}
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900 transition-colors"><X size={24} /></button>
        </div>
        
        <form className="p-8 space-y-8 overflow-y-auto" onSubmit={(e) => { e.preventDefault(); upsertEngagementMutation.mutate(formData); }}>
          {error && (
            <div className="p-4 bg-rose-50 border border-rose-100 rounded flex items-start gap-3 text-rose-600">
              <AlertCircle size={18} className="shrink-0 mt-0.5" />
              <p className="text-[11px] font-bold uppercase tracking-tight">{error}</p>
            </div>
          )}

          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input label="Name" required value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} placeholder="Engagement Title..." />
              
              <div>
                <SearchableSelect 
                  label="Parent Program Initiative"
                  options={allPrograms || []}
                  value={formData.program_id}
                  onChange={(val) => setFormData({ ...formData, program_id: val })}
                  placeholder="Select Program..."
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-1.5">
                <label className="block text-[9px] font-bold text-slate-400 uppercase tracking-widest ml-1">Engagement Structure</label>
                <select className="w-full px-4 py-2 bg-white border border-slate-200 rounded font-bold text-slate-900 text-sm outline-none focus:ring-4 focus:ring-slate-100" value={formData.category_id} onChange={e => setFormData({ ...formData, category_id: e.target.value })}>
                  <option value="">Select Structure...</option>
                  {categories?.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                </select>
              </div>
              <div className="space-y-1.5">
                <label className="block text-[9px] font-bold text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-2">
                  <Activity size={10} className="text-blue-500" /> Engagement Status
                </label>
                <select className="w-full px-4 py-2 bg-white border border-slate-200 rounded font-bold text-slate-900 text-sm outline-none focus:ring-4 focus:ring-slate-100" value={formData.status} onChange={e => setFormData({ ...formData, status: e.target.value })}>
                  <option value="in development">In Development</option>
                  <option value="in progress">In Progress</option>
                  <option value="delivered">Delivered</option>
                  <option value="on hold">On Hold</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
              <div className="space-y-1.5">
                <label className="block text-[9px] font-bold text-slate-400 uppercase tracking-widest ml-1">Attendance Strategy</label>
                <select className="w-full px-4 py-2 bg-white border border-slate-200 rounded font-bold text-slate-900 text-sm outline-none focus:ring-4 focus:ring-slate-100" value={formData.tracking_mode} onChange={e => setFormData({ ...formData, tracking_mode: e.target.value as TrackingMode })}>
                  <option value="roster">Managed Roster (Mark attendance for pre-linked personnel)</option>
                  <option value="drop_in">Drop-in (Search and register personnel on the fly)</option>
                  <option value="headcount">Headcount (Log total presence numbers only)</option>
                </select>
              </div>
              <div className="space-y-1.5">
                <label className="block text-[9px] font-bold text-slate-400 uppercase tracking-widest ml-1">Delivery Region</label>
                <select 
                  className="w-full px-4 py-2 bg-white border border-slate-200 rounded font-bold text-slate-900 text-sm outline-none focus:ring-4 focus:ring-slate-100" 
                  value={formData.region_id} 
                  onChange={e => setFormData({ ...formData, region_id: e.target.value })}
                >
                  <option value="">Select Region...</option>
                  {regions?.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6 pt-2">
              <div className="space-y-1.5">
                <label className="block text-[9px] font-bold text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-2"><Users2 size={10} /> Target Audience</label>
                <select 
                  className="w-full px-4 py-2 bg-white border border-slate-200 rounded font-bold text-slate-900 text-sm outline-none focus:ring-4 focus:ring-slate-100" 
                  value={formData.audience_type_id} 
                  onChange={e => setFormData({ ...formData, audience_type_id: e.target.value })}
                >
                  <option value="">Select Audience...</option>
                  {audienceTypes?.map(a => <option key={a.id} value={a.id}>{a.label}</option>)}
                </select>
              </div>
              <Input label="Roster Capacity" type="number" value={formData.capacity} onChange={e => setFormData({ ...formData, capacity: parseInt(e.target.value) || 0 })} />
            </div>

            <div className="grid grid-cols-2 gap-6 pt-2">
              <Input label="start date" type="date" required value={formData.start_date} onChange={e => setFormData({ ...formData, start_date: e.target.value })} />
              <Input label="end date" type="date" required value={formData.end_date} onChange={e => setFormData({ ...formData, end_date: e.target.value })} />
            </div>
          </div>

          <div className="space-y-4 pt-6 border-t border-slate-50">
             <h3 className="text-[9px] font-bold text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-2">
               <Building2 size={14} className="text-blue-500" /> Involvement Registry
             </h3>
             <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
                <input 
                  placeholder="Search Organizations..." 
                  className="w-full pl-10 pr-4 py-2 bg-white border border-slate-200 rounded font-bold outline-none text-xs uppercase" 
                  value={orgSearch} 
                  onChange={e => setOrgSearch(e.target.value)} 
                />
                {orgSearchResults && orgSearchResults.length > 0 && (
                  <div className="absolute z-20 w-full mt-1 bg-white border border-slate-200 rounded shadow-lg divide-y divide-slate-50 max-h-40 overflow-y-auto">
                     {orgSearchResults.map(o => (
                       <button type="button" key={o.id} onClick={() => addInvolvement(o)} className="w-full text-left p-3 hover:bg-slate-50 text-[11px] font-bold uppercase">{o.name}</button>
                     ))}
                  </div>
                )}
             </div>
             <div className="grid grid-cols-1 gap-2">
               {involvements.map(inv => (
                 <div key={inv.organization_id} className="p-3 bg-slate-50 rounded border border-slate-100 flex items-center justify-between">
                    <span className="text-[10px] font-bold text-slate-900 uppercase tracking-tight">{inv.name}</span>
                    <div className="flex items-center gap-2">
                       <select 
                         className="bg-white border border-slate-200 rounded px-2 py-1 text-[9px] font-black uppercase outline-none" 
                         value={inv.role_id} 
                         onChange={e => updateInvolvementRole(inv.organization_id, e.target.value)}
                       >
                          {orgRoles?.map(or => <option key={or.id} value={or.id}>{or.label}</option>)}
                       </select>
                       <button type="button" onClick={() => removeInvolvement(inv.organization_id)} className="p-1 text-slate-300 hover:text-rose-500 transition-colors"><Trash2 size={14} /></button>
                    </div>
                 </div>
               ))}
               {involvements.length === 0 && <p className="text-center py-4 text-[9px] font-bold text-slate-300 uppercase tracking-widest italic border border-dashed border-slate-200 rounded">No institutional links recorded</p>}
             </div>
          </div>

          <div className="space-y-6 pt-6 border-t border-slate-50">
            <h3 className="text-[9px] font-black uppercase text-slate-900 tracking-[0.2em] ml-1 flex items-center gap-2">
              <Tag size={12} className="text-slate-900" /> Engagement Tag(s)
            </h3>
            <div className="space-y-5">
              {tagGroups?.map(group => (
                <div key={group.id} className="space-y-3">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-black uppercase text-slate-900 tracking-widest bg-slate-100 px-2 py-0.5 rounded shadow-sm">{group.label}</span>
                    <div className="h-px bg-slate-100 flex-1"></div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {group.engagement_tags?.map((tag: any) => (
                      <button
                        key={tag.id}
                        type="button"
                        onClick={() => toggleTag(tag.id)}
                        className={`px-3 py-1.5 rounded text-[9px] font-black uppercase transition-all border ${selectedTagIds.includes(tag.id) ? 'bg-slate-900 text-[#FFDB00] border-slate-900 shadow-sm' : 'bg-white text-slate-400 border-slate-200 hover:border-slate-400'}`}
                      >
                        {tag.label}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
              {(!tagGroups || tagGroups.length === 0) && (
                <div className="py-8 text-center border-2 border-dashed border-slate-100 rounded-xl">
                   <p className="text-[9px] font-bold text-slate-300 uppercase italic">Configure tag groups in settings to begin categorization.</p>
                </div>
              )}
            </div>
          </div>

          <div className="space-y-1 pt-6 border-t border-slate-50">
            <label className="block text-[9px] font-bold text-slate-400 uppercase tracking-widest ml-1">Strategic Narrative</label>
            <textarea rows={3} className="w-full px-5 py-4 bg-white border border-slate-200 rounded font-bold text-slate-900 text-sm outline-none focus:ring-4 focus:ring-slate-100 transition-all placeholder:text-slate-200 resize-none shadow-inner" value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} placeholder="Engagement summary..." />
          </div>

          <div className="flex gap-3 pt-6 pb-4">
             <Button type="button" variant="ghost" className="flex-1 py-4" onClick={onClose}>Discard</Button>
             <Button type="submit" className="flex-[2] py-4 shadow-lg" isLoading={upsertEngagementMutation.isPending} icon={<Save size={18} />}>
               Commit Engagement
             </Button>
          </div>
        </form>
      </div>
    </div>
  );
};